import { AdminMediaUpdateButton } from '@components/AdminMediaUpdateButton';
import { SerenityCard } from '@components/Cards';
import { ContributorMediaModal } from '@components/ContributorMediaModal';
import NapsterSpinner from '@components/NapsterSpinner';
import Search from '@components/Search';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import useAlbumData from '@hooks/useAlbumData';
import { useFavorites } from '@hooks/useFavorites';
import useSerenityInfiniteScroll from '@hooks/useSerenityInfiniteScroll';
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { placeHolderData } from '../../config';
import { Album } from '../../types';
import { getTrackUrlForLocale } from '../../utils/localeUtils';

export const SerenityWellnessPage = memo(() => {
  const { t } = useTranslation();
  const { setActivePopup } = useCommonState();
  const { setActivePlaylist, addRecentTrack, preferredLocale } =
    useMusicState();
  const { toggleFavorite } = useFavorites();
  const { loadAlbumById } = useAlbumData();

  // State for genre filtering and search
  const [selectedGenre, setSelectedGenre] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [loadingAlbumId, setLoadingAlbumId] = useState<string | null>(null);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    album: Album | null;
  }>({ isOpen: false, album: null });

  const handleOpenModal = useCallback((album: Album) => {
    setModalState({ isOpen: true, album });
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalState({ isOpen: false, album: null });
  }, []);

  // Use shared infinite scroll hook - only load when page is active
  const {
    albums: serenityAlbums,
    loading: serenityLoading,
    hasMore,
    error,
    reset,
    sentinelRef,
  } = useSerenityInfiniteScroll({
    rootMargin: '200px', // Load more when 200px from bottom
    enabled: true, // Only load when this page is active
    genre: selectedGenre === 'All' ? undefined : selectedGenre,
    search: searchQuery.trim() || undefined,
  });

  const playAlbum = useCallback(
    async (album: Album) => {
      try {
        setLoadingAlbumId(album.id);
        // Fetch complete album data from API
        const albumData = await loadAlbumById(album.id);

        if (!albumData || !albumData.tracks || albumData.tracks.length === 0) {
          console.warn(
            'No tracks found in album:',
            albumData?.albumName || album.id,
          );
          setLoadingAlbumId(null);
          return;
        }

        // Get the first contributor for artist info
        const contributor =
          albumData.contributors && albumData.contributors.length > 0
            ? albumData.contributors[0]
            : null;
        const firstTrack = albumData.tracks[0];

        const playlist = {
          currentIndex: 0,
          album: albumData,
        };

        // Add first track to recent tracks when album is played
        const recentTrack = {
          track: {
            name: firstTrack.name,
            url: getTrackUrlForLocale(firstTrack, preferredLocale),
            trackIndex: 1,
          },
          contributor: {
            id: albumData.id,
            givenName: contributor?.givenName || 'Unknown Artist',
            thumbnailUrl:
              contributor?.thumbnailUrl || placeHolderData.contributorThumbnail,
          },
          albumArtUrl:
            albumData.albumArtUrl ||
            contributor?.thumbnailUrl ||
            placeHolderData.albumCover,
          timestamp: Date.now(),
        };
        addRecentTrack(recentTrack);

        // Store current page as previous page before opening music player
        setActivePlaylist(playlist);
        setActivePopup('musicplayer');
        setLoadingAlbumId(null);
      } catch (error) {
        console.error('Error loading album:', error);
        setLoadingAlbumId(null);
      }
    },
    [
      loadAlbumById,
      setActivePlaylist,
      setActivePopup,
      addRecentTrack,
      preferredLocale,
    ],
  );

  return (
    <div className="flex flex-col h-full pt-0 sm:pt-[10px]">
      {/* Fixed Header */}
      <div className="flex-shrink-0">
        <h1 className="text-[32px] sm:text-[40px] px-[24px] font-normal leading-[140%] text-left text-[#fcfcfc] font-['DM_Serif_Text']">
          {t('serenityPage.title')}
        </h1>
        <div className="text-[14px] px-[24px] font-normal leading-[140%] text-left text-white/80 pb-4 font-[NapsterSans]">
          {t('serenityPage.subtitle')}
        </div>
        {/* Search and Genre Filter */}
        <Search
          selectedGenre={selectedGenre}
          onGenreChange={setSelectedGenre}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          category="serenity"
        />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto na-scroll-container">
        <div className="px-4 sm:px-6 pb-[220px]">
          {/* Loading State - only show when page is active and no albums and loading */}
          {serenityLoading && serenityAlbums.length === 0 && (
            <div className="responsive-grid gap-4">
              {[...Array(8)].map((_, index) => (
                <div
                  key={`loading-${index}`}
                  className="flex flex-col animate-pulse">
                  <div className="w-full aspect-square rounded-[8px] bg-gray-700 mb-3"></div>
                  <div className="h-4 bg-gray-700 rounded mb-2"></div>
                  <div className="h-3 bg-gray-600 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-600 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State - show when no albums and not loading and no error */}
          {!serenityLoading && !error && serenityAlbums.length === 0 && (
            <div className="flex justify-center items-center py-12">
              <div className="text-center text-white/60">
                <div className="mb-4">
                  <svg
                    className="w-12 h-12 mx-auto mb-4 text-white/40"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33"
                    />
                  </svg>
                </div>
                <p className="text-lg font-medium mb-2">
                  {selectedGenre !== 'All' && searchQuery
                    ? t('serenityPage.noContentGenreSearch', { genre: selectedGenre, search: searchQuery })
                    : selectedGenre !== 'All'
                    ? t('serenityPage.noContentGenre', { genre: selectedGenre })
                    : searchQuery
                    ? t('serenityPage.noContentSearch', { search: searchQuery })
                    : t('serenityPage.noContentDefault')}
                </p>
                <p className="text-sm text-white/40">
                  {t('serenityPage.noContentHelp')}
                </p>
              </div>
            </div>
          )}

          {/* Content */}
          {serenityAlbums.length > 0 && (
            <div className="responsive-grid gap-6">
              {serenityAlbums?.map((album: Album, index: number) => {
                const contributor = album.contributors[0];
                const isLoading = loadingAlbumId === album.id;
                // Priority for poster image: album.videoMobilePreviewUrl -> contributor?.videoMobilePreviewUrl -> contributor?.thumbnailUrl
                const posterUrl =
                  album.videoMobilePreviewUrl ||
                  contributor?.videoMobilePreviewUrl ||
                  contributor?.thumbnailUrl;
                return (
                  <div
                    key={`serenity-album-${
                      album.id || album.albumName || index
                    }`}
                    className="flex flex-col relative">
                    <SerenityCard
                      album={album}
                      contributor={contributor}
                      videoUrl={
                        album.videoMobileUrl || contributor?.videoMobileUrl
                      }
                      posterUrl={posterUrl}
                      albumName={album.albumName}
                      likes={album.likes}
                      isLiked={album.isLiked}
                      onClick={() => playAlbum(album)}
                      onFavoriteClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(
                          'album',
                          album.id,
                          album.isLiked,
                          album.albumName,
                          contributor?.givenName,
                          album.category,
                        );
                      }}
                      showLikesCount={true}
                    />
                    {isLoading && (
                      <div className="absolute z-[100] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                        <NapsterSpinner />
                      </div>
                    )}
                    {/* Admin Media Update Button - top-right */}
                    <div className="absolute top-5 right-5 z-[999]">
                      <AdminMediaUpdateButton
                        album={album}
                        className="opacity-0 hover:opacity-100 transition-opacity duration-300"
                        onOpenModal={handleOpenModal}
                      />
                    </div>
                  </div>
                );
              })}

              {/* Infinite scroll sentinel */}
              {hasMore && (
                <div
                  ref={sentinelRef}
                  className="col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-4 flex justify-center py-8">
                  {serenityLoading ? (
                    <div className="flex flex-col items-center">
                      <NapsterSpinner size="medium" />
                      <div className="text-white/60 text-sm mt-2">
                        {t('serenityPage.loadingMore')}
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-4"></div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Error message for loading more items */}
          {error && serenityAlbums.length > 0 && (
            <div className="flex justify-center mt-6">
              <div className="text-red-400 text-center">
                <p className="text-sm">{t('serenityPage.failedToLoadMore')}</p>
                <button
                  onClick={() => reset()}
                  className="mt-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors duration-200">
                  {t('common.tryAgain')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Admin Media Update Modal */}
      {modalState.isOpen && modalState.album && (
        <ContributorMediaModal
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          contributor={modalState.album.contributors[0]}
          album={modalState.album}
        />
      )}
    </div>
  );
});

SerenityWellnessPage.displayName = 'SerenityWellnessPage';
